import { useLocation, NavLink, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { amountOwed, describeOwed, billedSentTotal } from '../lib/paymentPipeline'
import { countOpenChangeRequests } from '../lib/changeRequests'
import { visibleSections } from '../lib/clientSections'
import { useSidebarCollapsed } from '../hooks/useSidebarCollapsed'
import { vaultConfigured } from '../lib/vaultClient'
import RecentItems from './RecentItems'

// ── Icons ────────────────────────────────────────────────────────────────────
// One line-icon set for the whole shell, drawn on a 16px grid at 1.5 stroke.
// Every nav item names its icon; the Spaces reuse the same drawings inside a
// coloured tile so the sidebar reads as one family.

const ICONS = {
  home: <path d="M2.5 7.5L8 3l5.5 4.5v5.5h-11z" strokeLinejoin="round" />,
  inbox: <><path d="M2 9l2-6h8l2 6v4H2z" strokeLinejoin="round" /><path d="M2 9h3.5l1 1.5h3l1-1.5H14" strokeLinejoin="round" /></>,
  reviews: <><path d="M2.5 3h11v7.5h-6L4.5 13v-2.5h-2z" strokeLinejoin="round" /><path d="M5.5 6.5h5" strokeLinecap="round" /></>,
  chat: <><path d="M2 3h9v6H6l-2.5 2V9H2z" strokeLinejoin="round" /><path d="M11 6h3v5.5h-1V13l-2-1.5H8" strokeLinejoin="round" /></>,
  check: <><circle cx="8" cy="8" r="5.5" /><path d="M5.6 8l1.7 1.7 3.2-3.3" strokeLinecap="round" strokeLinejoin="round" /></>,
  clock: <><circle cx="8" cy="8" r="5.5" /><path d="M8 5v3l2 1.5" strokeLinecap="round" /></>,
  gear: <><circle cx="8" cy="8" r="2.2" /><path d="M8 2v1.8M8 12.2V14M14 8h-1.8M3.8 8H2M12.2 3.8l-1.3 1.3M5.1 10.9l-1.3 1.3M12.2 12.2l-1.3-1.3M5.1 5.1L3.8 3.8" strokeLinecap="round" /></>,
  users: <><circle cx="6" cy="6" r="2.3" /><path d="M2 13c.5-2.2 2-3.4 4-3.4s3.5 1.2 4 3.4" strokeLinecap="round" /><path d="M10.5 3.9a2.2 2.2 0 010 4.2M12 9.8c1 .5 1.7 1.6 2 3.2" strokeLinecap="round" /></>,
  user: <><circle cx="8" cy="5.5" r="2.6" /><path d="M3 13.5c.6-2.6 2.6-4 5-4s4.4 1.4 5 4" strokeLinecap="round" /></>,
  trend: <><path d="M2 12l4-4 2.5 2.5L14 5" strokeLinecap="round" strokeLinejoin="round" /><path d="M10.5 5H14v3.5" strokeLinecap="round" strokeLinejoin="round" /></>,
  board: <><rect x="2" y="2.5" width="12" height="11" rx="1.5" /><path d="M6 2.5v11M10 2.5v7" /></>,
  money: <><rect x="1.5" y="4" width="13" height="8" rx="1.5" /><circle cx="8" cy="8" r="1.8" /></>,
  library: <><path d="M2.5 3h3.5v10.5H2.5zM7 3h3.5v10.5H7z" strokeLinejoin="round" /><path d="M11.4 4l2.2-.6 1.6 9.5-2.2.5z" strokeLinejoin="round" /></>,
  chart: <><path d="M2.5 13.5h11" strokeLinecap="round" /><path d="M4.5 11V8M8 11V4.5M11.5 11V6.5" strokeLinecap="round" /></>,
  doc: <><path d="M4 1.8h5.5L12 4.3v9.9H4z" strokeLinejoin="round" /><path d="M6 8h4M6 10.5h4" strokeLinecap="round" /></>,
  tag: <><path d="M2.5 2.5h5.3l5.7 5.7-5.3 5.3-5.7-5.7z" strokeLinejoin="round" /><circle cx="5.3" cy="5.3" r="1" /></>,
  flag: <path d="M3.5 14V2.5M3.5 3h8.5l-1.8 3 1.8 3H3.5" strokeLinecap="round" strokeLinejoin="round" />,
  list: <path d="M2.5 4h11M2.5 8h11M2.5 12h11" strokeLinecap="round" />,
  folder: <path d="M2 4.5h4l1.5 1.5H14v6.5H2z" strokeLinejoin="round" />,
  receipt: <><path d="M3.5 1.8h9v12.4l-2.2-1.3-2.3 1.3-2.3-1.3-2.2 1.3z" strokeLinejoin="round" /><path d="M6 6h4M6 9h4" strokeLinecap="round" /></>,
  card: <><rect x="1.5" y="3.5" width="13" height="9" rx="1.5" /><path d="M1.5 6.5h13" /></>,
  wallet: <><path d="M2 4.5h11.5v8H2z" strokeLinejoin="round" /><path d="M2 4.5l8.5-2v2M10.5 8.5h3" strokeLinecap="round" /></>,
  calendar: <><rect x="2" y="3" width="12" height="11" rx="1.5" /><path d="M2 6.5h12M5.5 1.8v2.4M10.5 1.8v2.4" strokeLinecap="round" /></>,
  bell: <><path d="M4 11V7a4 4 0 018 0v4l1.2 1.5H2.8z" strokeLinejoin="round" /><path d="M6.5 14a1.6 1.6 0 003 0" strokeLinecap="round" /></>,
  globe: <><circle cx="8" cy="8" r="5.8" /><path d="M2.2 8h11.6M8 2.2c1.6 1.7 2.4 3.6 2.4 5.8S9.6 12.1 8 13.8C6.4 12.1 5.6 10.2 5.6 8S6.4 3.9 8 2.2z" strokeLinejoin="round" /></>,
  refresh: <><path d="M13 8a5 5 0 11-1.5-3.6" strokeLinecap="round" /><path d="M13 2.5v2.8h-2.8" strokeLinecap="round" strokeLinejoin="round" /></>,
  funnel: <path d="M2 2.5h12l-4.6 5.6v4.9l-2.8 1.5V8.1z" strokeLinejoin="round" />,
  heart: <path d="M8 13s-5.5-3.4-5.5-7A3 3 0 018 4.3 3 3 0 0113.5 6c0 3.6-5.5 7-5.5 7z" strokeLinejoin="round" />,
  pen: <><path d="M11 2l3 3-8.5 8.5H2.5v-3z" strokeLinejoin="round" /><path d="M9 4l3 3" /></>,
  sparkle: <path d="M8 1.8l1.5 4.2 4.2 1.5-4.2 1.5L8 13.2 6.5 9 2.3 7.5 6.5 6z" strokeLinejoin="round" />,
  mic: <><rect x="5.5" y="1.8" width="5" height="8" rx="2.5" /><path d="M3 8a5 5 0 0010 0M8 13v1.5" strokeLinecap="round" /></>,
  zap: <path d="M9.5 1.5L4 9h4.5l-2 5.5L12 7H7.5z" strokeLinejoin="round" />,
  activity: <path d="M1.5 8h2.5l2-4.5 3 9 2-4.5h3.5" strokeLinecap="round" strokeLinejoin="round" />,
  template: <><rect x="2" y="2.5" width="12" height="11" rx="1.5" /><path d="M2 6h12M6 6v7.5" /></>,
  contract: <><path d="M4 1.8h5.5L12 4.3v9.9H4z" strokeLinejoin="round" /><path d="M6 10.5l1.2 1.2 2.6-2.6" strokeLinecap="round" strokeLinejoin="round" /></>,
  search: <><circle cx="7" cy="7" r="4.6" /><path d="M10.5 10.5l3 3" strokeLinecap="round" /></>,
  plus: <path d="M8 3v10M3 8h10" strokeLinecap="round" />,
  chev: <path d="M5 6.5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />,
  panel: <><rect x="2" y="2.5" width="12" height="11" rx="1.5" /><path d="M6 2.5v11" /><path d="M10.5 6.5L9 8l1.5 1.5" strokeLinecap="round" strokeLinejoin="round" /></>,
  panelOpen: <><rect x="2" y="2.5" width="12" height="11" rx="1.5" /><path d="M6 2.5v11" /><path d="M9 6.5L10.5 8 9 9.5" strokeLinecap="round" strokeLinejoin="round" /></>,
  signout: <><path d="M6.5 2.5H3.5v11h3" strokeLinecap="round" strokeLinejoin="round" /><path d="M10 5l3 3-3 3M13 8H6.5" strokeLinecap="round" strokeLinejoin="round" /></>,
  help: <><circle cx="8" cy="8" r="5.8" /><path d="M6.4 6.3a1.7 1.7 0 113.2.8c-.5.6-1.6.9-1.6 2" strokeLinecap="round" /><circle cx="8" cy="11.4" r="0.5" fill="currentColor" stroke="none" /></>,
}

export function Icon({ name, size = 16, strokeWidth = 1.5, style }) {
  return (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth={strokeWidth} style={{ flexShrink: 0, ...style }}>
      {ICONS[name] || ICONS.list}
    </svg>
  )
}

// ── Nav item ─────────────────────────────────────────────────────────────────

function isActivePath(pathname, path) {
  if (pathname === path) return true
  // A record under a list (/projects/<id>) keeps its list lit. /dashboard is
  // excluded: everyone's home is /dashboard and nothing nests under it.
  return path !== '/dashboard' && pathname.startsWith(path + '/')
}

function Badge({ value, tone = 'red' }) {
  if (value == null || value <= 0) return null
  return (
    <span style={{
      background: tone === 'red' ? 'var(--red-dark)' : 'var(--surf)',
      color: tone === 'red' ? '#fff' : 'var(--txt)',
      fontSize: 11, fontWeight: 700, fontFamily: 'var(--fu)',
      padding: '1px 7px', borderRadius: 10, minWidth: 18, textAlign: 'center', boxSizing: 'border-box',
    }}>{value}</span>
  )
}

// `sub` nests a row one step further, under the row above it (the review
// pages under Projects), with a rule on the left so the family reads as one.
function NavItem({ path, label, icon = 'list', badge, badgeTone, child = false, sub = false, collapsed = false }) {
  const location = useLocation()
  const active = isActivePath(location.pathname, path)
  return (
    <NavLink
      to={path}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      className={`sidebar-nav-item${active ? ' is-active' : ''}`}
      data-tour={`nav-${path.replace(/^\//, '')}`}
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: child ? 8 : 10,
        justifyContent: collapsed ? 'center' : 'flex-start',
        height: collapsed ? 40 : (child ? 30 : 34),
        // Collapsed rows span the rail edge to edge, so hover and active fill it.
        width: collapsed ? '100%' : 'auto',
        margin: collapsed ? 0 : '1px 0',
        padding: collapsed ? 0 : (sub ? '0 10px 0 58px' : child ? '0 10px 0 40px' : '0 10px'),
        backgroundImage: sub && !collapsed ? 'linear-gradient(var(--bdr2), var(--bdr2))' : undefined,
        backgroundSize: sub && !collapsed ? '1px 100%' : undefined,
        backgroundPosition: sub && !collapsed ? '47px 0' : undefined,
        backgroundRepeat: 'no-repeat',
        borderRadius: 7,
        fontFamily: 'var(--fu)',
        fontSize: child ? 13 : 14,
        color: active ? 'var(--navy)' : 'var(--side-txt)',
        fontWeight: active ? 700 : 500,
        boxShadow: active ? (collapsed ? 'inset 3px 0 0 var(--red)' : 'var(--shadow-xs), 0 0 0 1px var(--bdr)') : 'none',
        textDecoration: 'none',
        transition: 'background 0.12s',
      }}
    >
      <span style={{ display: 'flex', color: child && !active ? 'var(--mut)' : 'inherit' }}>
        <Icon name={icon} size={collapsed ? 18 : (child ? 14 : 16)} />
      </span>
      {!collapsed && <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>}
      {!collapsed && <Badge value={badge} tone={badgeTone} />}
      {collapsed && badge > 0 && (
        <span style={{ position: 'absolute', top: 3, right: 3, width: 8, height: 8, borderRadius: 4, background: 'var(--red-dark)' }} />
      )}
    </NavLink>
  )
}

// A plain row that opens something instead of navigating (the Inbox panel).
function NavButton({ label, icon, badge, onClick, collapsed }) {
  return (
    <button
      type="button" onClick={onClick}
      title={collapsed ? label : undefined} aria-label={collapsed ? label : undefined}
      className="sidebar-nav-item"
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 10, justifyContent: collapsed ? 'center' : 'flex-start',
        height: collapsed ? 40 : 34, width: '100%', margin: collapsed ? 0 : '1px 0',
        padding: collapsed ? 0 : '0 10px', borderRadius: 7, border: 'none', background: 'transparent',
        fontFamily: 'var(--fu)', fontSize: 14, fontWeight: 500, color: 'var(--side-txt)', textAlign: 'left',
      }}
    >
      <Icon name={icon} size={collapsed ? 18 : 16} />
      {!collapsed && <span style={{ flex: 1 }}>{label}</span>}
      {!collapsed && <Badge value={badge} />}
      {collapsed && badge > 0 && <span style={{ position: 'absolute', top: 3, right: 3, width: 8, height: 8, borderRadius: 4, background: 'var(--red-dark)' }} />}
    </button>
  )
}

// ── Space (a coloured, collapsible group) ───────────────────────────────────

function Space({ label, icon, color, items, collapsed }) {
  const location = useLocation()
  const navigate = useNavigate()
  const hasActive = items.some(i => isActivePath(location.pathname, i.path))
  const storeKey = `ac_space_open_${label}`
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(storeKey); if (v != null) return v === '1' } catch { /* private mode */ }
    return hasActive
  })
  useEffect(() => { if (hasActive) setOpen(true) }, [location.pathname]) // eslint-disable-line
  function toggle() {
    setOpen(o => {
      try { localStorage.setItem(storeKey, o ? '0' : '1') } catch { /* private mode */ }
      return !o
    })
  }
  const badgeTotal = items.reduce((n, i) => n + (i.badge > 0 && i.badgeTone !== 'muted' ? i.badge : 0), 0)

  const tile = (
    <span style={{
      width: collapsed ? 28 : 20, height: collapsed ? 28 : 20, borderRadius: collapsed ? 7 : 5,
      // A light tile with the Space's colour in the icon, not a dark block.
      background: 'var(--surf)', border: '1px solid var(--bdr)', boxSizing: 'border-box', color: color === '#1A1A1A' ? 'var(--txt)' : color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      boxShadow: collapsed && hasActive ? `0 0 0 2px var(--side-bg), 0 0 0 4px ${color}` : 'none',
    }}>
      <Icon name={icon} size={collapsed ? 15 : 12} strokeWidth={1.7} />
    </span>
  )

  if (collapsed) {
    // A rail has no room for children: the tile goes to the Space's first page.
    return (
      <button type="button" title={label} aria-label={label}
        onClick={() => navigate(items[0].path)}
        style={{ position: 'relative', width: '100%', height: 40, margin: 0, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        className="sidebar-nav-item">
        {tile}
        {badgeTotal > 0 && <span style={{ position: 'absolute', top: 2, right: 2, width: 8, height: 8, borderRadius: 4, background: 'var(--red-dark)' }} />}
      </button>
    )
  }

  // The space you are in sits on a panel a shade darker than the sidebar,
  // so "where am I" reads at a glance; the current page keeps its own mark.
  return (
    <div className={hasActive ? 'space-active' : undefined} style={hasActive ? {
      background: 'color-mix(in srgb, var(--txt) 6%, var(--side-bg))', margin: '2px -6px', padding: '2px 6px 6px',
    } : undefined}>
      <button type="button" onClick={toggle} aria-expanded={open}
        className="sidebar-nav-item"
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 34, margin: '1px 0', padding: '0 10px',
          border: 'none', borderRadius: 7, background: 'transparent', textAlign: 'left',
          fontFamily: 'var(--fu)', fontSize: 14, fontWeight: hasActive ? 700 : 600, color: 'var(--txt)',
        }}>
        {tile}
        <span style={{ flex: 1 }}>{label}</span>
        {!open && <Badge value={badgeTotal} />}
        <span style={{ display: 'flex', color: 'var(--mut)', transition: 'transform 0.15s', transform: open ? 'none' : 'rotate(-90deg)' }}>
          <Icon name="chev" size={12} />
        </span>
      </button>
      {open && items.map(item => <NavItem key={item.path} child {...item} />)}
    </div>
  )
}

function SectionLabel({ children, collapsed, action }) {
  if (collapsed) return <span aria-hidden="true" style={{ display: 'block', width: 28, height: 1, background: 'var(--bdr2)', margin: '10px auto' }} />
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '16px 10px 6px', fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--mut)' }}>
      <span>{children}</span>{action}
    </div>
  )
}

// The block every sidebar opens with: who this is, and the collapse control.
// The studio's own mark, not initials in a box: the horizontal lockup when the
// sidebar is open, the triangle alone in the icon strip. The lockup's
// "CREATIVE" is dark ink, so dark mode swaps in the white version (index.css,
// .brand-logo-*).
function Brand({ title, subtitle, collapsed, onToggle }) {
  if (collapsed) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        {/* Collapsed, the solid red mark fills the rail edge to edge and sits
            flush with the top, like a tab of the brand colour. */}
        <img src="/logo-mark-solid.svg" alt="Abstract Creative" style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '1 / 1' }} />
        <button type="button" onClick={onToggle} aria-label="Expand sidebar" title="Expand sidebar  [" className="sidebar-nav-item"
          style={{ width: '100%', height: 36, border: 'none', background: 'transparent', color: 'var(--mut)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="panelOpen" />
        </button>
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 2px 12px 8px', marginBottom: 6, borderBottom: '1px solid var(--bdr)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Cropped copies of the lockup, so the height is all logo: the
            originals carry a wide margin that made it read small. */}
        <img src="/logo-lockup.svg" alt="Abstract Creative" className="brand-logo-light" style={{ display: 'block', height: 40, width: 'auto', maxWidth: '100%' }} />
        <img src="/logo-lockup-light.svg" alt="Abstract Creative" className="brand-logo-dark" style={{ height: 40, width: 'auto', maxWidth: '100%' }} />
        {/* A client's or contractor's portal names whose portal it is. */}
        {title && <div style={{ fontFamily: 'var(--fu)', fontSize: 13, fontWeight: 700, color: 'var(--txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 6 }}>{title}</div>}
        {subtitle && <div style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--mut)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>{subtitle}</div>}
      </div>
      {onToggle && (
        <button type="button" onClick={onToggle} aria-label="Collapse sidebar" title="Collapse sidebar  [" className="sidebar-nav-item"
          style={{ width: 32, height: 32, border: 'none', background: 'transparent', color: 'var(--mut)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name="panel" />
        </button>
      )}
    </div>
  )
}

function SidebarFrame({ isOpen, onClose, collapsed, children }) {
  return (
    <>
      {isOpen && <div className="sidebar-backdrop" onClick={onClose} />}
      <aside className={`sidebar${isOpen ? ' sidebar-open' : ''}${collapsed ? ' sidebar-collapsed' : ''}`} style={{
        width: 'var(--sidebar-width)', background: 'var(--side-bg)', borderRight: '1px solid var(--bdr)',
        position: 'fixed', top: 0, left: 0, bottom: 0, boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', zIndex: 100, overflowY: 'auto', overflowX: 'hidden',
        padding: collapsed ? '0 0 12px' : '12px 10px',
      }}>
        {children}
      </aside>
    </>
  )
}

function SignOutRow({ signOut, collapsed }) {
  return (
    <button type="button" onClick={signOut} className="sidebar-nav-item"
      title={collapsed ? 'Sign out' : undefined} aria-label={collapsed ? 'Sign out' : undefined}
      style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: collapsed ? 'center' : 'flex-start', width: '100%', height: collapsed ? 40 : 34, margin: collapsed ? 0 : '1px 0', padding: collapsed ? 0 : '0 10px', border: 'none', borderRadius: 7, background: 'transparent', fontFamily: 'var(--fu)', fontSize: 14, color: 'var(--mut)', textAlign: 'left' }}>
      <Icon name="signout" size={collapsed ? 18 : 16} />{!collapsed && 'Sign out'}
    </button>
  )
}

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n)


function NotificationsPanel({ open, onClose, revisionCount, unreadMessages, recentlyPaidCount = 0, failedEmails = 0, onMarkAllRead }) {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('All')

  if (!open) return null

  const showApprovals = activeTab === 'All' || activeTab === 'Approvals'
  const showMessages  = activeTab === 'All' || activeTab === 'Messages'
  const showFinance   = activeTab === 'All' || activeTab === 'Finance'

  const isEmpty = (
    (activeTab === 'All'       && revisionCount === 0 && unreadMessages === 0 && recentlyPaidCount === 0 && failedEmails === 0) ||
    (activeTab === 'Approvals' && revisionCount === 0) ||
    (activeTab === 'Messages'  && unreadMessages === 0) ||
    (activeTab === 'Finance'   && recentlyPaidCount === 0)
  )

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.3)' }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 360, background: 'var(--card)',
        zIndex: 201, boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column',
        animation: 'slideInRight 0.2s ease',
      }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(100%) } to { transform: translateX(0) } }`}</style>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--bdr)' }}>
          <div style={{ fontFamily: 'var(--fd)', fontSize: 17, fontWeight: 600, color: 'var(--navy)' }}>Notifications</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {(unreadMessages > 0 || revisionCount > 0 || recentlyPaidCount > 0 || failedEmails > 0) && (
              <span style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--red)', cursor: 'pointer' }} onClick={onMarkAllRead}>
                Mark all read
              </span>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--mut)', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
        </div>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--bdr)', padding: '0 20px' }}>
          {['All', 'Approvals', 'Messages', 'Finance'].map(tab => (
            <div key={tab} onClick={() => setActiveTab(tab)}
              style={{ padding: '10px 12px', fontFamily: 'var(--fu)', fontSize: 12, color: activeTab === tab ? 'var(--navy)' : 'var(--mut)', borderBottom: activeTab === tab ? '2px solid var(--red)' : '2px solid transparent', cursor: 'pointer', fontWeight: activeTab === tab ? 600 : 400, transition: 'color 0.15s' }}>
              {tab}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {(activeTab === 'All' || activeTab === 'Messages') && failedEmails > 0 && (
            <div
              onClick={() => { navigate('/communications'); onClose() }}
              style={{ display: 'flex', gap: 10, padding: '12px 14px', margin: '0 0 8px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 9, cursor: 'pointer' }}
            >
              <div style={{ width: 7, height: 7, borderRadius: 4, background: '#DC2626', marginTop: 5, flexShrink: 0 }} />
              <div>
                <div style={{ fontFamily: 'var(--fu)', fontSize: 12.5, fontWeight: 600, color: '#B91C1C' }}>
                  {failedEmails} update{failedEmails !== 1 ? 's' : ''} failed to send
                </div>
                <div style={{ fontFamily: 'var(--fu)', fontSize: 11, color: '#B91C1C', marginTop: 2, lineHeight: 1.45 }}>
                  Posted to the portal but never delivered. Open Communications to retry.
                </div>
              </div>
            </div>
          )}

          {showApprovals && revisionCount > 0 && (
            <div onClick={() => { onClose(); navigate('/revisions') }} style={{ display: 'flex', gap: 12, padding: '14px 20px', borderBottom: '1px solid var(--bdr)', background: 'rgba(209,56,51,0.03)', cursor: 'pointer' }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="7.5" r="6" stroke="#854D0E" strokeWidth="1.3"/><path d="M5 7.5l2 2 3-3" stroke="#854D0E" strokeWidth="1.3" strokeLinecap="round"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--fb)', fontSize: 13, fontWeight: 600, color: 'var(--txt)', marginBottom: 2 }}>
                  {revisionCount} open change{revisionCount !== 1 ? 's' : ''} from clients
                </div>
                <div style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--mut)' }}>Review notes and approvals sent back, all in Revisions</div>
                <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--red)', marginTop: 3 }}>View revisions →</div>
              </div>
            </div>
          )}
          {showMessages && unreadMessages > 0 && (
            <div onClick={() => { onClose(); navigate('/messages') }} style={{ display: 'flex', gap: 12, padding: '14px 20px', borderBottom: '1px solid var(--bdr)', background: 'rgba(209,56,51,0.03)', cursor: 'pointer' }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1.5" y="3" width="12" height="9" rx="1.5" stroke="#1D4ED8" strokeWidth="1.3"/><path d="M1.5 3l6 4.5 6-4.5" stroke="#1D4ED8" strokeWidth="1.2"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--fb)', fontSize: 13, fontWeight: 600, color: 'var(--txt)', marginBottom: 2 }}>
                  {unreadMessages} unread message{unreadMessages !== 1 ? 's' : ''}
                </div>
                <div style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--mut)' }}>Clients are waiting for a reply</div>
                <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--red)', marginTop: 3 }}>View messages →</div>
              </div>
            </div>
          )}
          {showFinance && recentlyPaidCount > 0 && (
            <div onClick={() => { onClose(); navigate('/invoices') }} style={{ display: 'flex', gap: 12, padding: '14px 20px', borderBottom: '1px solid var(--bdr)', background: 'rgba(5,150,105,0.03)', cursor: 'pointer' }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="7.5" r="6" stroke="#166534" strokeWidth="1.3"/><path d="M5 7.5l2 2 3-3" stroke="#166534" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--fb)', fontSize: 13, fontWeight: 600, color: 'var(--txt)', marginBottom: 2 }}>
                  {recentlyPaidCount} payment{recentlyPaidCount !== 1 ? 's' : ''} received
                </div>
                <div style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--mut)' }}>Invoice{recentlyPaidCount !== 1 ? 's' : ''} marked paid in the last 7 days</div>
                <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--red)', marginTop: 3 }}>View in Invoices →</div>
              </div>
            </div>
          )}
          {isEmpty && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--mut)', fontFamily: 'var(--fu)', fontSize: 13 }}>
              {activeTab === 'All' ? 'All caught up — no new notifications.' : `No ${activeTab.toLowerCase()} notifications.`}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ── Admin Sidebar ─────────────────────────────────────────────────────────────

function AdminSidebar({ user, signOut, isOpen, onClose }) {
  const email = user?.email || ''
  const fullName = user?.user_metadata?.full_name || user?.user_metadata?.name || ''
  const displayName = fullName || email.split('@')[0] || 'Admin'
  const [stats, setStats] = useState({ billedYTD: 0, activeClients: 0, uncollected: 0, owedNote: null, proposalsOut: 0 })
  const [onboardingCount, setOnboardingCount] = useState(0)
  const [pendingApprovals, setPendingApprovals] = useState(0)
  const [overdueInvoices, setOverdueInvoices] = useState(0)
  const [recentlyPaidCount, setRecentlyPaidCount] = useState(0)
  const [revisionCount, setRevisionCount] = useState(0)
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [failedEmails, setFailedEmails] = useState(0)
  const [, setLogoUrl] = useState(null)
  const [showNotifications, setShowNotifications] = useState(false)
  const location = useLocation()

  // Close sidebar on navigation (mobile)
  useEffect(() => { onClose?.() }, [location.pathname])

  useEffect(() => {
    async function loadStats() {
      try {
        const thisYear = new Date().getFullYear()
        const yearStart = `${thisYear}-01-01`

        const now = new Date()
        const nowISO = now.toISOString()

        const [
          { data: allInvoices },
          { data: allProjects },
          { data: allClients },
          { data: approvals },
          { data: proposals },
        ] = await Promise.all([
          // One invoice query, carrying everything the four figures below need:
          // the status and due date for overdue, the amount and date for billed,
          // and the project/proposal each one belongs to so a payment can be
          // netted against the price it was a payment on.
          supabase.from('invoices').select('id, amount, status, due_date, created_at, project_id, proposal_id, description, invoice_number'),
          supabase.from('projects').select('id, title, contract_value, proposal_id'),
          // Active clients — not pending approval
          supabase.from('clients').select('id, approved').is('archived_at', null),
          supabase.from('approvals').select('id, status'),
          supabase.from('proposals').select('id, client_id, status, total, amount_paid, title, proposal_number'),
        ])

        const invoices = allInvoices || []

        // A draft has been sent to nobody, so it is not money billed. Counting
        // it made Billed YTD $600 higher than the sum of every invoice a client
        // has actually seen.
        const billed = billedSentTotal(invoices.filter(i => i.created_at >= yearStart))

        // Not "which invoices are unpaid" — that came to $0 while $7,959 of
        // accepted work sat unpaid, because three of the four engagements had
        // never been invoiced at all. This counts money owed: each agreed price
        // less what has been paid against it, plus any unpaid invoice belonging
        // to no engagement.
        const owed = amountOwed({ projects: allProjects || [], proposals: proposals || [], invoices })
        const uncollected = owed.total
        const active = (allClients || []).filter(c => c.approved !== false).length

        const overdue = invoices.filter(i =>
          i.status !== 'paid' && i.status !== 'draft' && i.due_date && new Date(i.due_date) < now
        ).length

        const apr = approvals || []
        const prop = proposals || []
        const pending = apr.filter(a => a.status === 'pending').length
        const proposalsOut = prop.filter(p => ['sent', 'viewed', 'pending'].includes(p.status)).length

        setStats({ billedYTD: billed, activeClients: active, uncollected, owedNote: describeOwed(owed), proposalsOut })
        setPendingApprovals(pending)
        setOverdueInvoices(overdue)
        // Every open change request, not only approvals sent back: review notes
        // from documents, websites and videos count too. Same loader as the
        // Revisions page, so the badge and the page never disagree.
        countOpenChangeRequests(supabase).then(setRevisionCount).catch(() => {})

        const { data: onb } = await supabase.from('clients')
          .select('id, onboarding_complete, onboarding_enabled')
          .eq('onboarding_complete', false)
        setOnboardingCount(onb?.filter(c => c.onboarding_enabled !== false).length || 0)

        // Recently paid invoices (last 7 days, after last acknowledgement)
        const sevenDaysAgo = new Date()
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
        const paidAck = localStorage.getItem('ac_paid_ack')
        const paidSince = paidAck && new Date(paidAck) > sevenDaysAgo
          ? paidAck.split('T')[0]
          : sevenDaysAgo.toISOString().split('T')[0]
        const { data: paidRecently } = await supabase
          .from('invoices').select('id')
          .eq('status', 'paid')
          .gte('paid_date', paidSince)
        setRecentlyPaidCount(paidRecently?.length || 0)

        // Updates whose email failed. email_error is cleared by a successful
        // retry, so a row still carrying one is still undelivered.
        try {
          const { data: failed } = await supabase
            .from('project_updates').select('id')
            .eq('status', 'published')
            .not('email_error', 'is', null)
          setFailedEmails(failed?.length || 0)
        } catch { /* older installs may not have the column */ }

        // Unread messages from clients
        try {
          const { data: msgs } = await supabase.from('messages').select('id').eq('sender', 'client').eq('read', false)
          setUnreadMessages(msgs?.length || 0)
        } catch { /* messages table may not exist yet */ }
      } catch (err) {
        console.error('Sidebar stats error:', err)
      }
    }
    loadStats()
    supabase.from('app_settings').select('value').eq('key', 'logo_url').single()
      .then(({ data }) => { if (data?.value) setLogoUrl(data.value) })
  }, [])

  async function markAllRead() {
    try {
      await supabase.from('messages').update({ read: true }).eq('sender', 'client').eq('read', false)
      setUnreadMessages(0)
    } catch { /* ignore */ }
    localStorage.setItem('ac_paid_ack', new Date().toISOString())
    setRecentlyPaidCount(0)
    setRevisionCount(0)
    setShowNotifications(false)
  }

  const [collapsed, setCollapsed] = useSidebarCollapsed()
  const toggle = () => setCollapsed(c => !c)
  const notificationCount = unreadMessages + revisionCount + recentlyPaidCount + failedEmails

  // Every admin page lives in exactly one place. Five Spaces hold the work;
  // the rows above them are what a person checks several times a day.
  const spaces = [
    { label: 'Clients', icon: 'users', color: '#183C44', items: [
      { path: '/clients-overview', label: 'Overview', icon: 'home' },
      { path: '/admin', label: 'All clients', icon: 'users' },
      // Onboarding shows only while someone is in it; the page stays reachable.
      ...(onboardingCount > 0 ? [{ path: '/onboarding', label: 'Onboarding', icon: 'clock', badge: onboardingCount, badgeTone: 'muted' }] : []),
      { path: '/networking', label: 'Networking', icon: 'user' },
      // Nudges and surveys are tabs inside Client health.
      { path: '/health', label: 'Client health', icon: 'heart' },
      { path: '/reports', label: 'Client updates', icon: 'doc' },
      { path: '/communications', label: 'Communications', icon: 'chat' },
      { path: '/referrals', label: 'Referrals & reviews', icon: 'sparkle' },
    ] },
    { label: 'Sales', icon: 'trend', color: '#EF4237', items: [
      { path: '/sales', label: 'Overview', icon: 'home' },
      { path: '/leads', label: 'Leads', icon: 'funnel' },
      { path: '/proposals', label: 'Proposals', icon: 'doc' },
      { path: '/price-list', label: 'Price list', icon: 'tag' },
      { path: '/rfp-inbox', label: 'RFP inbox', icon: 'inbox' },
      { path: '/bid-pipeline', label: 'Bid pipeline', icon: 'flag' },
      { path: '/bids', label: 'Bid records', icon: 'list' },
      { path: '/contracts', label: 'Contracts', icon: 'contract' },
      { path: '/templates', label: 'Templates', icon: 'template' },
    ] },
    { label: 'Work', icon: 'board', color: '#1A1A1A', items: [
      { path: '/projects', label: 'Projects', icon: 'board' },
      // Everything a client looks at and answers, kept together under Projects.
      { path: '/reviews-inbox', label: 'All reviews', icon: 'reviews', sub: true },
      { path: '/website-review', label: 'Website', icon: 'globe', sub: true },
      { path: '/document-reviews', label: 'Documents', icon: 'doc', sub: true },
      { path: '/reviews-inbox?type=VID', label: 'Videos', icon: 'reviews', sub: true },
      { path: '/approvals', label: 'Approvals', icon: 'check', sub: true },
      { path: '/revisions', label: 'Revisions', icon: 'refresh', badge: revisionCount, sub: true },
      { path: '/project-templates', label: 'Project templates', icon: 'template' },
      { path: '/requests', label: 'Portal requests', icon: 'inbox' },
      { path: '/meetings', label: 'Meetings', icon: 'mic' },
      { path: '/schedule', label: 'Schedule', icon: 'calendar' },
      { path: '/time-review', label: 'Where time went', icon: 'chart' },
      { path: '/contractors', label: 'Contractors', icon: 'user' },
      { path: '/reviews', label: 'Contractor reviews', icon: 'check' },
    ] },
    { label: 'Money', icon: 'money', color: '#5E8A91', items: [
      { path: '/money', label: 'Overview', icon: 'home' },
      { path: '/books', label: 'Finance hub', icon: 'home' },
      { path: '/invoices', label: 'Invoices', icon: 'receipt', badge: overdueInvoices },
      { path: '/expenses', label: 'Expenses', icon: 'wallet' },
      { path: '/retainers', label: 'Retainers', icon: 'refresh' },
      { path: '/profitability', label: 'Profitability', icon: 'trend' },
      { path: '/forecast', label: 'Forecast', icon: 'chart' },
      { path: '/tax-forms', label: 'Tax forms (1099)', icon: 'doc' },
    ] },
    { label: 'Library', icon: 'library', color: '#8C7462', items: [
      { path: '/files', label: 'Files & assets', icon: 'folder' },
      { path: '/portfolio-media', label: 'Portfolio media', icon: 'folder' },
      { path: '/brand-assets', label: 'Brand assets', icon: 'sparkle' },
      { path: '/vault', label: 'Brand vault', icon: 'folder' },
      { path: '/brand-strategy', label: 'Brand strategy', icon: 'sparkle' },
      { path: '/content-studio', label: 'Content studio', icon: 'pen' },
      { path: '/social-calendar', label: 'Social calendar', icon: 'calendar' },
    ] },
    { label: 'Insights', icon: 'chart', color: '#C7352B', items: [
      { path: '/insights', label: 'Overview', icon: 'home' },
      { path: '/metrics', label: 'Metrics & next steps', icon: 'chart' },
      { path: '/analytics', label: 'Marketing channels', icon: 'trend' },
      { path: '/portal-activity', label: 'Portal activity', icon: 'activity' },
    ] },
  ]

  return (
    <SidebarFrame isOpen={isOpen} onClose={onClose} collapsed={collapsed}>
      <Brand subtitle={displayName} collapsed={collapsed} onToggle={toggle} />
      <NotificationsPanel open={showNotifications} onClose={() => setShowNotifications(false)} revisionCount={revisionCount} unreadMessages={unreadMessages} recentlyPaidCount={recentlyPaidCount} failedEmails={failedEmails} onMarkAllRead={markAllRead} />

      <nav aria-label="Workspace" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <NavItem path="/dashboard" label="Home" icon="home" collapsed={collapsed} />
        <NavButton label="Inbox" icon="inbox" badge={notificationCount} onClick={() => setShowNotifications(true)} collapsed={collapsed} />
        <NavItem path="/messages" label="Messages" icon="chat" badge={unreadMessages} collapsed={collapsed} />
        <NavItem path="/reviews-inbox" label="Reviews" icon="reviews" badge={pendingApprovals} badgeTone="muted" collapsed={collapsed} />
        <NavItem path="/time" label="Timesheets" icon="clock" collapsed={collapsed} />

        {/* The records last opened, and any pinned: getting back to the thing
            open five minutes ago should not mean finding it in a list again. */}
        <RecentItems collapsed={collapsed} />

        <SectionLabel collapsed={collapsed}>Spaces</SectionLabel>
        {spaces.map(s => <Space key={s.label} {...s} collapsed={collapsed} />)}

        <div style={{ flex: 1, minHeight: 16 }} />
        <NavItem path="/automations" label="Automations" icon="zap" collapsed={collapsed} />
        <NavItem path="/cms" label="Website CMS" icon="globe" collapsed={collapsed} />
        <NavItem path="/settings" label="Settings" icon="gear" collapsed={collapsed} />
        <SignOutRow signOut={signOut} collapsed={collapsed} />
      </nav>

      {!collapsed && (
        <dl title="Uncollected: every agreed price, less what has been paid on it — including work not invoiced yet."
          style={{ margin: '12px 0 0', padding: '10px 10px 2px', borderTop: '1px solid var(--bdr)', display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 4, columnGap: 8, fontFamily: 'var(--fu)', fontSize: 12 }}>
          <dt style={{ color: 'var(--mut)' }}>Billed YTD</dt><dd style={{ margin: 0, fontWeight: 700, color: 'var(--txt)' }}>{fmt(stats.billedYTD)}</dd>
          <dt style={{ color: 'var(--mut)' }}>Uncollected</dt><dd style={{ margin: 0, fontWeight: 700, color: 'var(--txt)' }}>{fmt(stats.uncollected)}</dd>
          <dt style={{ color: 'var(--mut)' }}>Active clients</dt><dd style={{ margin: 0, fontWeight: 700, color: 'var(--txt)' }}>{stats.activeClients}</dd>
          <dt style={{ color: 'var(--mut)' }}>Proposals out</dt><dd style={{ margin: 0, fontWeight: 700, color: 'var(--txt)' }}>{stats.proposalsOut}</dd>
        </dl>
      )}
    </SidebarFrame>
  )
}

// ── Client Sidebar ─────────────────────────────────────────────────────────────

const CLIENT_LOGO_FALLBACK = '/logo-stacked.svg'

function ClientSidebar({ client, signOut, isOpen, onClose }) {
  const [project, setProject] = useState(null)
  const [pendingApprovals, setPendingApprovals] = useState(0)
  const [overdueInvoices, setOverdueInvoices] = useState(0)
  const [pendingBrief, setPendingBrief] = useState(null)
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [openForms, setOpenForms] = useState(0)
  const [, setLogoUrl] = useState(CLIENT_LOGO_FALLBACK)
  // What this client actually has. Sections with nothing behind them are left
  // out of the nav rather than offered and empty.
  const [sectionCounts, setSectionCounts] = useState({})
  const [countsLoaded, setCountsLoaded] = useState(false)
  const location = useLocation()

  // Close sidebar on navigation (mobile)
  useEffect(() => { onClose?.() }, [location.pathname])

  const nameParts = client?.name?.split(' ') || []
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  useEffect(() => {
    if (!client?.id) return
    async function load() {
      // `head: true` with an exact count returns the number and no rows, so
      // deciding what to show costs a count per section rather than the rows.
      const owned = t => supabase.from(t).select('id', { count: 'exact', head: true }).eq('client_id', client.id)

      const [
        { data: projects }, { data: approvals }, { data: invoices },
        { data: logoSetting }, { data: brief }, { data: unreadMsgs },
        onboarding, briefs, approvalsAll, reviews, brandAssets, proposals, meetings,
        formsAll, formsOpen,
      ] = await Promise.all([
        supabase.from('projects').select('title, progress, stage').eq('client_id', client.id).neq('stage', 'delivered').limit(1),
        supabase.from('approvals').select('id').eq('client_id', client.id).eq('status', 'pending'),
        supabase.from('invoices').select('id').eq('client_id', client.id).eq('status', 'overdue'),
        supabase.from('app_settings').select('value').eq('key', 'logo_url').single(),
        supabase.from('project_briefs').select('id, projects(title)').eq('client_id', client.id).eq('status', 'pending').order('created_at', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('messages').select('id').eq('client_id', client.id).eq('sender', 'admin').eq('read', false),
        owned('onboarding_steps'), owned('project_briefs'), owned('approvals'),
        owned('website_reviews'), owned('brand_assets'), owned('proposals'), owned('meetings'),
        owned('client_forms'), owned('client_forms').eq('status', 'open'),
      ])
      if (projects?.length) setProject(projects[0])
      setPendingApprovals(approvals?.length || 0)
      setOverdueInvoices(invoices?.length || 0)
      setPendingBrief(brief || null)
      setUnreadMessages(unreadMsgs?.length || 0)
      setOpenForms(formsOpen?.count || 0)
      if (logoSetting?.value) setLogoUrl(logoSetting.value)
      setSectionCounts({
        onboarding: onboarding.count || 0,
        briefs: briefs.count || 0,
        approvals: approvalsAll.count || 0,
        reviews: reviews.count || 0,
        brandAssets: brandAssets.count || 0,
        proposals: proposals.count || 0,
        meetings: meetings.count || 0,
        forms: formsAll?.count || 0,
      })
      setCountsLoaded(true)
    }
    load()
  }, [client])

  // Whether this client has a brand vault on the NAS. Read from vault_clients
  // (a client can see only their own row), so the nav offers the vault only
  // once the studio has switched it on.
  const [hasVault, setHasVault] = useState(false)
  useEffect(() => {
    if (!client?.id || !vaultConfigured()) return
    supabase.from('vault_clients').select('enabled').eq('client_id', client.id).maybeSingle()
      .then(({ data }) => setHasVault(!!data && data.enabled !== false))
  }, [client?.id])

  // Realtime unread message badge
  useEffect(() => {
    if (!client?.id) return
    const channel = supabase
      .channel('client-sidebar-msgs')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `client_id=eq.${client.id}` }, payload => {
        if (payload.new.sender === 'admin') setUnreadMessages(n => n + 1)
      })
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [client?.id])

  const navItems = [
    { path: '/dashboard',  label: 'Home',            icon: 'home' },
    client?.onboarding_enabled !== false && { path: '/onboarding', label: 'Onboarding', icon: 'clock' },
    { path: '/requests',   label: 'Requests',       icon: 'inbox' },
    { path: '/projects',   label: 'Projects',       icon: 'board' },
    { path: '/forms',      label: 'Forms',          icon: 'doc',      badge: openForms },
    { path: '/approvals',  label: 'Approvals',      icon: 'reviews',  badge: pendingApprovals },
    { path: '/website-review', label: 'Website review', icon: 'globe' },
    { path: '/brand-assets', label: 'Brand kit',     icon: 'sparkle' },
    vaultConfigured() && { path: '/vault', label: 'Brand vault', icon: 'folder' },
    { path: '/files',      label: 'Files & assets', icon: 'folder' },
    client?.analytics_enabled && { path: '/analytics', label: 'Analytics', icon: 'chart' },
    { path: '/messages',   label: 'Messages',       icon: 'chat',     badge: unreadMessages },
    { path: '/proposals',  label: 'Proposals',      icon: 'doc' },
    { path: '/invoices',   label: 'Invoices',       icon: 'receipt',  badge: overdueInvoices },
    { path: '/meetings',   label: 'Meeting notes',  icon: 'mic' },
  ].filter(Boolean)

  // A section with nothing behind it is left out rather than offered empty.
  // The routes still resolve, so a link already sent to this client keeps
  // working — this only changes what the nav advertises.
  const visibleNav = visibleSections(navItems, { ...sectionCounts, vault: hasVault ? 1 : 0 }, { loaded: countsLoaded })
  const [collapsed, setCollapsed] = useSidebarCollapsed()
  const toggle = () => setCollapsed(c => !c)
  const initials = (firstName[0] || '') + (lastName[0] || '')

  return (
    <SidebarFrame isOpen={isOpen} onClose={onClose} collapsed={collapsed}>
      <Brand title={client?.company || [firstName, lastName].filter(Boolean).join(' ') || 'Client portal'}
        subtitle={client?.company ? `${firstName} ${lastName}`.trim() : 'Abstract Creative portal'}
        initials={(initials || 'AC').toUpperCase()} collapsed={collapsed} onToggle={toggle} />

      {!collapsed && pendingApprovals > 0 && (
        <NavLink to="/approvals" style={{ textDecoration: 'none', display: 'block', margin: '4px 0 6px' }}>
          <div style={{ padding: '10px 12px', background: 'var(--red-bg)', border: '1px solid var(--bdr2)', borderRadius: 9, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ display: 'flex', color: 'var(--red-dark)' }}><Icon name="reviews" /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--fu)', fontSize: 13, fontWeight: 700, color: 'var(--txt)', lineHeight: 1.25 }}>
                {pendingApprovals === 1 ? '1 item needs your review' : `${pendingApprovals} items need your review`}
              </div>
              <div style={{ fontFamily: 'var(--fu)', fontSize: 12, color: 'var(--mut)', marginTop: 1 }}>Open approvals</div>
            </div>
          </div>
        </NavLink>
      )}

      {!collapsed && pendingBrief && (
        <NavLink to="/brief" style={{ textDecoration: 'none', display: 'block', margin: '0 0 6px' }}>
          <div style={{ padding: '10px 12px', background: 'var(--warning-bg)', border: '1px solid var(--bdr2)', borderRadius: 9, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ display: 'flex', color: 'var(--warning)' }}><Icon name="doc" /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--fu)', fontSize: 13, fontWeight: 700, color: 'var(--txt)', lineHeight: 1.25 }}>Project brief needed</div>
              <div style={{ fontFamily: 'var(--fu)', fontSize: 12, color: 'var(--mut)', marginTop: 1 }}>Fill out your brief</div>
            </div>
          </div>
        </NavLink>
      )}

      <nav aria-label="My portal" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {visibleNav.map(item => <NavItem key={item.path} {...item} collapsed={collapsed} />)}
        <div style={{ flex: 1, minHeight: 16 }} />
        {!collapsed && project && (
          <div style={{ margin: '0 0 8px', padding: '12px', background: 'var(--card)', border: '1px solid var(--bdr)', borderRadius: 9 }}>
            <div style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 700, color: 'var(--mut)', marginBottom: 4 }}>Current project</div>
            <div style={{ fontFamily: 'var(--fu)', fontSize: 13, fontWeight: 700, color: 'var(--txt)', marginBottom: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.title}</div>
            <div style={{ height: 5, background: 'var(--surf)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${project.progress || 0}%`, background: 'var(--accent)', transition: 'width 0.4s ease' }} />
            </div>
            <div style={{ fontFamily: 'var(--fu)', fontSize: 12, color: 'var(--mut)', marginTop: 4 }}>{project.progress || 0}% complete</div>
          </div>
        )}
        <NavButton label="Show me around" icon="help" onClick={() => window.dispatchEvent(new CustomEvent('ac:start-tour'))} collapsed={collapsed} />
        <SignOutRow signOut={signOut} collapsed={collapsed} />
      </nav>
    </SidebarFrame>
  )
}

// ── Contractor Sidebar ─────────────────────────────────────────────────────────

function ContractorSidebar({ contractor, signOut, isOpen, onClose }) {
  const [toSign, setToSign] = useState(0)
  const [collapsed, setCollapsed] = useSidebarCollapsed()
  const location = useLocation()

  useEffect(() => { onClose?.() }, [location.pathname])

  // The badge is what is waiting on the contractor: agreements sent for
  // signature. (It used to count assignments the studio left at "pending",
  // which is the studio's state, not theirs.)
  useEffect(() => {
    if (!contractor?.id) return
    supabase.from('contractor_agreements').select('id').eq('contractor_id', contractor.id).eq('status', 'sent')
      .then(({ data, error }) => setToSign(error ? 0 : data?.length || 0))
  }, [contractor, location.pathname])

  const initials = contractor?.name?.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase() || '?'

  return (
    <SidebarFrame isOpen={isOpen} onClose={onClose} collapsed={collapsed}>
      <Brand title={contractor?.name || 'Contractor'} subtitle={contractor?.specialty || 'Contractor portal'}
        initials={initials} collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} />
      <nav aria-label="My work" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <NavItem path="/dashboard" label="Overview" icon="home" collapsed={collapsed} />
        <NavItem path="/my-projects" label="My projects" icon="board" collapsed={collapsed} />
        <NavItem path="/my-time" label="Time" icon="clock" collapsed={collapsed} />
        <NavItem path="/my-agreements" label="Agreements" icon="contract" badge={toSign} collapsed={collapsed} />
        <NavItem path="/earnings" label="Earnings" icon="wallet" collapsed={collapsed} />
        <NavItem path="/reviews" label="Reviews" icon="reviews" collapsed={collapsed} />
        <NavItem path="/w9" label="W-9 tax form" icon="doc" collapsed={collapsed} />
        <div style={{ flex: 1, minHeight: 16 }} />
        <SignOutRow signOut={signOut} collapsed={collapsed} />
      </nav>
    </SidebarFrame>
  )
}

// ── Export ────────────────────────────────────────────────────────────────────

export default function Sidebar({ isOpen, onClose }) {
  const { user, client, contractor, isAdmin, isContractor, isPreview, previewClient, previewContractor, signOut } = useAuth()

  if (isAdmin && !isPreview) {
    return <AdminSidebar user={user} signOut={signOut} isOpen={isOpen} onClose={onClose} />
  }
  if (isContractor || (isPreview && previewContractor)) {
    const effectiveContractor = isPreview ? previewContractor : contractor
    return <ContractorSidebar contractor={effectiveContractor} signOut={signOut} isOpen={isOpen} onClose={onClose} />
  }
  const effectiveClient = isPreview ? previewClient : client
  return <ClientSidebar client={effectiveClient} signOut={signOut} isOpen={isOpen} onClose={onClose} />
}
